import { toolRegistry, ExecutionContext } from "../../tools/registry";
import { registerBuiltinTools } from "../../tools/builtin";

export async function runTools(args: string[]) {
  // Initialize tools
  registerBuiltinTools();
  
  const action = args[0] || "list";
  const toolName = args[1];

  switch (action) {
    case "list":
      await listTools();
      break;
    case "show":
      if (!toolName) {
        console.error("Usage: metiscode tools show <name>");
        process.exitCode = 1;
        return;
      }
      await showTool(toolName);
      break;
    case "test":
      if (!toolName) {
        console.error("Usage: metiscode tools test <name> [params...]");
        process.exitCode = 1;
        return;
      }
      await testTool(toolName, args.slice(2));
      break;
    case "exec":
      if (!toolName) {
        console.error("Usage: metiscode tools exec <name> <params...>");
        process.exitCode = 1;
        return;
      }
      await execTool(toolName, args.slice(2));
      break;
    default:
      console.log(`
Usage: metiscode tools [action] [name] [params...]

Actions:
  list       List available tools (default)
  show       Show tool details
  test       Test tool execution (dry run)
  exec       Execute tool with parameters
`);
  }
}

async function listTools() {
  const format = process.env.METIS_FORMAT || "pretty";
  const tools = toolRegistry.list();
  
  if (format === "json") {
    const toolData = tools.map(name => {
      const tool = toolRegistry.get(name)!;
      return {
        name: tool.name,
        description: tool.description,
        category: tool.metadata?.category,
        requires_approval: tool.safety?.require_approval || false
      };
    });
    console.log(JSON.stringify(toolData, null, 2));
    return;
  }
  
  if (tools.length === 0) {
    console.log("No tools registered.");
    return;
  }
  
  // Group by category
  const byCategory: Record<string, any[]> = {};
  
  for (const toolName of tools) {
    const tool = toolRegistry.get(toolName)!;
    const category = tool.metadata?.category || "uncategorized";
    
    if (!byCategory[category]) {
      byCategory[category] = [];
    }
    
    byCategory[category].push({
      name: tool.name,
      description: tool.description,
      requiresApproval: tool.safety?.require_approval || false,
      allowedInCI: tool.safety?.allowed_in_ci !== false
    });
  }
  
  console.log(`Available tools (${tools.length}):`);
  
  for (const [category, categoryTools] of Object.entries(byCategory)) {
    console.log(`\n📁 ${category}:`);
    
    for (const tool of categoryTools) {
      const approvalIcon = tool.requiresApproval ? "🔐" : "✅";
      const ciIcon = tool.allowedInCI ? "🤖" : "🚫";
      console.log(`  ${approvalIcon}${ciIcon} ${tool.name} - ${tool.description}`);
    }
  }
  
  console.log(`\nLegend: 🔐=requires approval, ✅=auto-approved, 🤖=CI allowed, 🚫=CI blocked`);
  console.log(`Use: metiscode tools show <name> for details`);
}

async function showTool(name: string) {
  const tool = toolRegistry.get(name);
  if (!tool) {
    console.error(`Tool not found: ${name}`);
    process.exitCode = 1;
    return;
  }
  
  const format = process.env.METIS_FORMAT || "pretty";
  
  if (format === "json") {
    console.log(JSON.stringify(tool, null, 2));
    return;
  }
  
  console.log(`Tool: ${tool.name}`);
  console.log(`Description: ${tool.description}`);
  console.log(`Category: ${tool.metadata?.category || "uncategorized"}`);
  console.log(`Version: ${tool.metadata?.version || "unknown"}`);
  
  if (tool.safety) {
    console.log("\n🔒 Safety Policy:");
    console.log(`  Requires approval: ${tool.safety.require_approval ? "yes" : "no"}`);
    console.log(`  Network access: ${tool.safety.network_access ? "yes" : "no"}`);
    console.log(`  Allowed in CI: ${tool.safety.allowed_in_ci !== false ? "yes" : "no"}`);
    console.log(`  Max execution time: ${tool.safety.max_execution_time || "unlimited"}ms`);
    
    if (tool.safety.path_restrictions && tool.safety.path_restrictions.length > 0) {
      console.log(`  Path restrictions: ${tool.safety.path_restrictions.join(", ")}`);
    }
  }
  
  if (tool.schema) {
    console.log("\n📋 Parameters:");
    if (tool.schema.properties) {
      for (const [param, spec] of Object.entries(tool.schema.properties as any)) {
        const required = tool.schema.required?.includes(param) ? " (required)" : "";
        const defaultVal = spec.default !== undefined ? ` [default: ${spec.default}]` : "";
        console.log(`  ${param}: ${spec.type}${required}${defaultVal}`);
        if (spec.description) {
          console.log(`    ${spec.description}`);
        }
      }
    }
  }
}

async function testTool(name: string, paramArgs: string[]) {
  console.log(`🧪 Testing tool: ${name} (dry run)`);
  
  // Parse parameters (simplified - would need proper parsing for complex params)
  const params: Record<string, any> = {};
  for (let i = 0; i < paramArgs.length; i += 2) {
    if (paramArgs[i].startsWith('--')) {
      const key = paramArgs[i].slice(2);
      const value = paramArgs[i + 1] || "true";
      params[key] = value;
    }
  }
  
  const context: ExecutionContext = {
    sessionId: "test-session",
    workingDirectory: process.cwd(),
    config: { autoApprove: true },
    traceEnabled: true,
    verboseEnabled: process.env.METIS_VERBOSE === 'true'
  };
  
  console.log("Parameters:", params);
  console.log("Note: This would execute the tool with the given parameters");
  
  const tool = toolRegistry.get(name);
  if (tool && tool.safety?.require_approval) {
    console.log("⚠️  This tool requires approval in normal execution");
  }
}

async function execTool(name: string, paramArgs: string[]) {
  console.log(`⚡ Executing tool: ${name}`);
  
  // Parse parameters (simplified)
  const params: Record<string, any> = {};
  for (let i = 0; i < paramArgs.length; i += 2) {
    if (paramArgs[i].startsWith('--')) {
      const key = paramArgs[i].slice(2);
      const value = paramArgs[i + 1] || "true";
      params[key] = value;
    }
  }
  
  const context: ExecutionContext = {
    sessionId: `exec-${Date.now()}`,
    workingDirectory: process.cwd(),
    config: { autoApprove: process.env.METIS_AUTO_APPROVE === 'true' },
    traceEnabled: process.env.METIS_TRACE === 'true',
    verboseEnabled: process.env.METIS_VERBOSE === 'true'
  };
  
  try {
    const result = await toolRegistry.execute(name, params, context);
    
    if (result.success) {
      console.log("✅ Success");
      if (result.content) {
        console.log("\nResult:");
        console.log(typeof result.content === 'string' ? result.content : JSON.stringify(result.content, null, 2));
      }
      if (result.metadata) {
        console.log("\nMetadata:", result.metadata);
      }
    } else {
      console.log("❌ Failed");
      console.error("Error:", result.error);
      process.exitCode = 1;
    }
  } catch (error: any) {
    console.error("❌ Execution failed:", error.message);
    process.exitCode = 1;
  }
}